/**
 * Citation extraction + cross-collection resolution (DOM-free, Node + browser).
 *
 * This is the engine behind the app's "intertwined" law: it scans any text
 * (an opinion body, a rule, a statute, a curated summary) and finds the
 * statute / court-rule / case citations inside it, normalizing each to a stable
 * KEY. Callers resolve those keys against the three offline collections
 * (MGL statutes, the court-rules table, the opinions table + curated CASELAW)
 * to turn a plain citation into a clickable cross-link, and — via a build-time
 * index over the same keys — to answer "what else cites this?".
 *
 * The extractor is deliberately conservative: it anchors on hard tokens
 * (reporter abbreviations, `c.`/`§`, rule-set abbreviations) so arbitrary prose
 * doesn't produce false links. Anything it can't confidently normalize is left
 * as plain text.
 */

export type CiteKind = 'statute' | 'rule' | 'case';

export interface CitationMatch {
  /** The exact substring matched in the source text. */
  raw: string;
  /** Start offset of `raw` within the source text. */
  index: number;
  /** Length of `raw`. */
  length: number;
  kind: CiteKind;
  /** Normalized resolution key (see normalizeStatuteKey / RuleKey / caseKey). */
  key: string;
}

/* ------------------------------------------------------------------ statutes */

/** Normalize a chapter + section to a statute key, e.g. ("234A","67") -> "234A-67". */
export function statuteKey(chapter: string, section: string): string {
  return `${chapter.toUpperCase()}-${section.toUpperCase()}`;
}

// G.L. c. 234A, § 67  /  M.G.L. c. 94C, §§ 32-34  /  Mass. Gen. Laws c. 265, § 1
const STATUTE_RE =
  /(?:M\.?\s?G\.?\s?L\.?|G\.?\s?L\.?|Mass\.?\s?Gen\.?\s?Laws)\.?\s*c\.?\s*(\d{1,3}[A-Z]?)\s*,?\s*(?:§{1,2}|sections?|secs?\.?)\s*(\d+[A-Z]?)/gi;

/* --------------------------------------------------------------------- rules */

/**
 * Canonical rule-set slugs. These must match the slug derived at runtime from
 * the `rules` table's `rule_set` column (see ruleSetSlug()).
 */
export type RuleSetSlug =
  | 'crimp'
  | 'civ'
  | 'app'
  | 'sjc'
  | 'super'
  | 'dccrimp'
  | 'dcstanding'
  | 'dcspecial'
  | 'dcprobation'
  | 'dcappdiv'
  | 'tcimpound'
  | 'tcc123s35'
  | 'tcpubaccess'
  | 'tcmagistrate';

/** Normalize a rule set + number to a rule key, e.g. ("crimp","14") -> "crimp|14". */
export function ruleKey(slug: RuleSetSlug, number: string): string {
  return `${slug}|${number.replace(/\s+/g, '')}`;
}

/**
 * Reduce a `rules.rule_number` value to the bare token a citation would name.
 *
 * The PDF-sourced sets store "Rule 14" and the SJC set "Rule 1:19", but the
 * browser-captured sets store the whole label — "District Court Standing Order
 * 1-25", "Uniform Magistrate Rule 6". Indexing those verbatim made their keys
 * unreachable: no citation in any text says "District Court Standing Order
 * 1-25" *as the number*, so nothing could ever resolve to them. Both sides of
 * the graph now run through this, so target keys and citation keys agree.
 */
export function ruleNumberToken(ruleNumber: string): string {
  const s = String(ruleNumber ?? '').trim();
  // "…Rule 14" / "…Order 1-25" / "Rule 1:19" — take what follows the last
  // Rule/Order token, which is the part a citation actually names.
  const m = s.match(/(?:^|\s)(?:Rule|Order)\s+([0-9][0-9A-Za-z.:/-]*)$/i);
  if (m) return m[1];
  // A bare number ("207", "1-18", "1:19") is already the token.
  if (/^[0-9][0-9A-Za-z.:/-]*$/.test(s)) return s;
  // Anything else (the Rule XIV "Addendum") keys on its own normalized label.
  return s.toLowerCase().replace(/\s+/g, '-');
}

/**
 * Map a `rules.rule_set` column value to its canonical slug. Used both to build
 * the runtime rule index and to keep it aligned with the citation abbreviations.
 *
 * Ordered most-specific first, and the browser-captured sets are tested BEFORE
 * the generic "…procedure" checks. That ordering is load-bearing: several of
 * their labels contain a word the generic tests look for — Trial Court Rule
 * XIII is a *civil commitment* set, not "civil procedure", and the Appellate
 * Division set is "appellate division appeals", not "appellate procedure".
 */
export function ruleSetSlug(ruleSet: string): RuleSetSlug | null {
  const s = ruleSet.toLowerCase();
  if (s.includes('district court standing order')) return 'dcstanding';
  if (s.includes('special rules') && s.includes('district')) return 'dcspecial';
  if (s.includes('probation violation')) return 'dcprobation';
  if (s.includes('appellate division')) return 'dcappdiv';
  if (s.includes('impoundment')) return 'tcimpound';
  if (s.includes('civil commitment')) return 'tcc123s35';
  if (s.includes('public access')) return 'tcpubaccess';
  if (s.includes('magistrate')) return 'tcmagistrate';
  if (s.includes('criminal procedure') && s.includes('district')) return 'dccrimp';
  if (s.includes('criminal procedure')) return 'crimp';
  if (s.includes('civil procedure')) return 'civ';
  if (s.includes('appellate procedure')) return 'app';
  if (s.includes('supreme judicial court')) return 'sjc';
  if (s.includes('superior court')) return 'super';
  return null;
}

// Each entry: a regex whose FIRST capture group is the rule number, plus the
// slug it resolves to. Ordered most-specific first.
const RULE_RES: { re: RegExp; slug: RuleSetSlug }[] = [
  { re: /Mass\.?\s?R\.?\s?Crim\.?\s?P\.?\s*(\d+[A-Z]?(?:\.\d+)?)/gi, slug: 'crimp' },
  { re: /Mass\.?\s?R\.?\s?Civ\.?\s?P\.?\s*(\d+[A-Z]?(?:\.\d+)?)/gi, slug: 'civ' },
  { re: /Mass\.?\s?R\.?\s?A(?:pp)?\.?\s?P\.?\s*(\d+[A-Z]?(?:\.\d+)?)/gi, slug: 'app' },
  { re: /(?:S\.?J\.?C\.?\s?Rule|Supreme Judicial Court Rule)\s*(\d+(?::\d+)?)/gi, slug: 'sjc' },
  { re: /(?:Superior Court Rule|Mass\.?\s?Super\.?\s?Ct\.?\s?R\.?)\s*(\d+[A-Z]?)/gi, slug: 'super' },
  // Browser-captured sets. Only forms that name a SPECIFIC rule are matched:
  // each of these sets stores one document per rule, so a set-level reference
  // ("Trial Court Rule VIII") has no single target and is deliberately left as
  // plain text rather than pointed at an arbitrary member rule.
  //
  // The court prefix on the standing-order pattern is required, not decorative.
  // The corpus also contains "Superior Court Standing Order 5-81" and
  // "Superior Court Standing Order 1-22", which are a different court's orders
  // and are NOT in this set — a bare /Standing Order \d+-\d+/ would link them
  // to whatever District Court order shares the number. Bare "Standing Order
  // 1-09" is likewise ambiguous and left alone.
  // The ordinal/"Amended" slot is spelled out rather than a generic word
  // wildcard: these regexes carry /i, so `[A-Z][a-z]+` would match ANY word and
  // quietly swallow whatever preceded "Standing Order" — including the
  // "Superior Court" the court prefix is there to exclude.
  {
    re: /(?:District(?:\/Municipal)? Court|Joint)\s+(?:(?:First|Second|Third|Fourth|Fifth|Sixth|Seventh|Eighth|Ninth|Tenth)\s+)?(?:Amended\s+)?Standing Order\s*(\d+-\d+)/gi,
    slug: 'dcstanding'
  },
  { re: /District Court Special Rule\s*(\d+)/gi, slug: 'dcspecial' },
  { re: /Uniform Magistrate Rule\s*(\d+)/gi, slug: 'tcmagistrate' },
];

/* --------------------------------------------------------------------- cases */

/** Canonical reporter tokens we recognize in a case citation. */
const REPORTERS: { re: string; token: string }[] = [
  { re: 'Mass\\.\\s?App\\.\\s?Ct\\.', token: 'mass-app-ct' },
  { re: 'Mass\\.', token: 'mass' },
  { re: 'U\\.?\\s?S\\.', token: 'us' },
  { re: 'S\\.\\s?Ct\\.', token: 's-ct' },
  { re: 'L\\.\\s?Ed\\.\\s?2d', token: 'l-ed-2d' },
  { re: 'F\\.\\s?(?:2d|3d|4th)', token: 'f' },
  { re: 'F\\.\\s?Supp\\.\\s?(?:2d|3d)?', token: 'f-supp' },
  { re: 'N\\.\\s?E\\.\\s?(?:2d|3d)?', token: 'ne' },
];

// e.g. "485 Mass. 711", "71 Mass. App. Ct. 258", "174 F.3d 1", "443 U.S. 307"
const CASE_RE = new RegExp(
  `\\b(\\d{1,3})\\s+(${REPORTERS.map((r) => r.re).join('|')})\\s+(\\d{1,4})\\b`,
  'gi'
);

/** Normalize a reporter phrase (as matched) to its canonical token. */
function reporterToken(matched: string): string | null {
  const norm = matched.replace(/\s+/g, ' ').trim();
  for (const r of REPORTERS) {
    if (new RegExp(`^${r.re}$`, 'i').test(norm)) return r.token;
  }
  return null;
}

/** Normalize a reporter citation to a case key, e.g. "485|mass|711". */
export function caseKey(volume: string, reporterToken: string, page: string): string {
  return `${volume}|${reporterToken}|${page}`;
}

/* ----------------------------------------------------------------- extractor */

/**
 * Extract every statute / rule / case citation from `text`, in source order,
 * de-overlapped (earlier, longer matches win). Each match carries a normalized
 * `key` for resolution.
 */
export function extractCitations(text: string): CitationMatch[] {
  if (!text) return [];
  const out: CitationMatch[] = [];

  const push = (m: RegExpExecArray, kind: CiteKind, key: string | null): void => {
    if (!key) return;
    out.push({ raw: m[0], index: m.index, length: m[0].length, kind, key });
  };

  // Statutes.
  for (const m of text.matchAll(STATUTE_RE)) {
    push(m as RegExpExecArray, 'statute', statuteKey(m[1], m[2]));
  }
  // Rules.
  for (const { re, slug } of RULE_RES) {
    for (const m of text.matchAll(re)) {
      push(m as RegExpExecArray, 'rule', ruleKey(slug, m[1]));
    }
  }
  // Cases.
  for (const m of text.matchAll(CASE_RE)) {
    const tok = reporterToken(m[2]);
    push(m as RegExpExecArray, 'case', tok ? caseKey(m[1], tok, m[3]) : null);
  }

  // Sort by position; drop matches that overlap an already-kept, earlier match.
  out.sort((a, b) => a.index - b.index || b.length - a.length);
  const kept: CitationMatch[] = [];
  let cursor = -1;
  for (const c of out) {
    if (c.index >= cursor) {
      kept.push(c);
      cursor = c.index + c.length;
    }
  }
  return kept;
}

/* ----------------------------------------------------------------- resolution */

export interface ResolvedTarget {
  kind: CiteKind;
  /** Stable doc id in its collection (opinion/rule doc_id, curated CASELAW id). */
  docId?: string;
  /** External source URL (statutes via MGL, or any doc's official source). */
  url?: string;
  /** Human label for the link target. */
  title?: string;
  /** Which collection satisfied the lookup. */
  source: 'db' | 'mgl' | 'curated';
}

/** Lookup maps keyed by the normalized citation keys above. */
export interface CiteIndexes {
  statutes?: Map<string, ResolvedTarget>;
  rules?: Map<string, ResolvedTarget>;
  cases?: Map<string, ResolvedTarget>;
}

/** Resolve one extracted citation against the provided indexes, or null. */
export function resolveCitation(m: CitationMatch, idx: CiteIndexes): ResolvedTarget | null {
  const map = m.kind === 'statute' ? idx.statutes : m.kind === 'rule' ? idx.rules : idx.cases;
  return map?.get(m.key) ?? null;
}
